import { Company } from '~/models/Company';
import { MaybeRef, toValue } from 'vue';
import { getEconomicGroupMembers } from '~/services/apiCompany';
import { useAuthStore } from './useAuthStore';
import { useEventStore } from './useEventStore';

export type TCompanyStore = ReturnType<typeof useCompanyStore>;

export const useCompanyStore = defineStore(
  'MY_SCAN_SOURCE_COMPANY_STORE',
  () => {
    const AUTH_STORE = useAuthStore();
    const EVENT_STORE = useEventStore();

    const { pagination: companyListPagination, CallPaginate: CallCompanyListPaginate } =
      usePaginator();

    const companyList = ref<Company[]>([]);
    const selectedCompany = ref<Company | null>(null);

    const hasSelectedCompany = computed(() => !!selectedCompany.value);
    const companyName = computed(() => selectedCompany.value?.companyName);
    const companyDocument = computed(() => selectedCompany.value?.companyDocument);
    const sessionCompany = computed(() => {
      const sessionCompany = companyList.value.find(
        (item) =>
          item.companyDocument.unmasked === AUTH_STORE.session.currentEconomicGroupMember,
      );

      return sessionCompany ?? null;
    });

    function setCompanyList(list: Company[]) {
      companyList.value = [...list];
    }

    function selectCompany(company: MaybeRef<Company | null>) {
      const _company = toValue(company);
      if (!_company) return false;

      if (
        AUTH_STORE.session.currentEconomicGroupMember !==
          sessionCompany.value?.companyDocument.unmasked &&
        !selectedCompany.value
      ) {
        selectBySession();
        return true;
      }

      if (!selectedCompany.value) {
        selectedCompany.value = _company;
        AUTH_STORE.session.setCurrentEconomicGroupMember(
          selectedCompany.value?.companyDocument.unmasked,
        );
        EVENT_STORE.process('OnChangeEconomicGroup', true);
        return true;
      }

      if (selectedCompany.value.id === _company.id) {
        return false;
      } else {
        selectedCompany.value = _company;
        AUTH_STORE.session.setCurrentEconomicGroupMember(
          selectedCompany.value?.companyDocument.unmasked,
        );
        EVENT_STORE.process('OnChangeEconomicGroup', true);
        return true;
      }
    }

    function clearCompanyData() {
      companyList.value = [];
      selectedCompany.value = null;
    }

    function selectBySession() {
      if (!AUTH_STORE.session) throw new Error('Session not found');
      selectCompany(sessionCompany as MaybeRef<Company | null>);
    }

    async function dispatchGetEconomicGroupMembers(economicGroup: string[]) {
      await getEconomicGroupMembers(economicGroup).then((data) => {
        setCompanyList(data);
        selectCompany(sessionCompany as MaybeRef<Company | null>);
      });
    }

    return {
      selectedCompany,
      companyName,
      companyDocument,
      companyList,
      companyListPagination,
      hasSelectedCompany,
      setCompanyList,
      selectCompany,
      CallCompanyListPaginate,
      clearCompanyData,
      dispatchGetEconomicGroupMembers,
      onSessionOpen: async ({ economicGroupMembers }: Session) =>
        await dispatchGetEconomicGroupMembers(economicGroupMembers),
      onSessionClose: clearCompanyData,
    };
  },
  { persist: true },
);
